import type { RefObject } from 'react';
import {
    Alert,
    Box,
    Button,
    Form,
    FormButtonRow,
    FormGroup,
    Heading,
    Link,
    PasswordInput,
    Text,
    TextInput,
    UserSelect,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ILogin } from '@common/interfaces/login';
import { getSelectedLanguage, translateKey } from '@frontend/translate';
import type { ValidationErrors } from '../../../utils/validation';
import type { IUser } from '../../interfaces/user';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';

export enum LoginTypes {
    firstCSLogin = 'firstCSLogin',
    regularCSLogin = 'regularCSLogin',
}

interface ILoginCredentialsProps {
    buttonRef: RefObject<HTMLButtonElement>;
    errorCode?: string;
    errors: ValidationErrors;
    formRef: RefObject<HTMLFormElement>;
    handleChange: <K extends keyof ILogin>(name: K, value: ILogin[K]) => void;
    handleChangeUserSelect: (value: string) => Promise<void>;
    isFormDisabled: boolean;
    isLoginButtonDisabled?: boolean;
    login: ILogin;
    loginType: LoginTypes;
    onDoValidate: (name: keyof ILogin) => void;
    onSubmit: () => void;
    remainingAttempts: number | null;
    rememberedUsers?: IUser[];
    userNamePrefilled: boolean;
}

export function LoginCredentials(props: ILoginCredentialsProps) {
    const translate = getSelectedLanguage();
    const [showRememberedUsers, setShowRememberedUsers] = useState(
        !props.userNamePrefilled && !!props.rememberedUsers?.length && props.loginType === LoginTypes.regularCSLogin
    );

    useEffect(() => {
        if (props.errors.size > 0) {
            handleFocusChangeOnError();

            if (props.errors.get('userName')) {
                setShowRememberedUsers(false);
            }
        }
    }, [props.errors]);

    return (
        <>
            <Heading>Log in als zorgprofessional</Heading>
            {props.loginType === LoginTypes.firstCSLogin && (
                <Text>
                    De volgende keer gaat het inloggen via het HIS vanzelf. Deze keer vragen we je nog om in te loggen.
                </Text>
            )}
            {showErrorMessage()}
            <Form
                formRef={props.formRef}
                id="LoginCredentials"
                name="LoginCredentials"
                onSubmit={(e) => {
                    e.preventDefault();
                    props.onSubmit();
                }}
                submitOnEnter={true}
            >
                {showRememberedUsers && (
                    <FormGroup
                        label="Selecteer je account"
                        layout="vertical"
                    >
                        <UserSelect
                            data-dd-action-name="remembered-login-username"
                            onChange={(username) => props.handleChangeUserSelect(username)}
                            onDismiss={() => {
                                props.handleChangeUserSelect('');
                                setShowRememberedUsers(false);
                            }}
                            users={props.rememberedUsers ?? []}
                            value={props.login.userName}
                        />
                    </FormGroup>
                )}

                {!showRememberedUsers && (
                    <FormGroup
                        hasErrors={props.errors.has('userName')}
                        label="Gebruikersnaam"
                        layout="vertical"
                    >
                        <TextInput
                            data-dd-action-name="login-username"
                            disabled={props.isFormDisabled}
                            error={
                                props.errors.get('userName')
                                    ? translateKey('validation.' + props.errors.get('userName'))
                                    : undefined
                            }
                            name="userName"
                            onBlur={() => props.onDoValidate('userName')}
                            onChange={(e) => props.handleChange('userName', e.target.value)}
                            type="text"
                            value={props.login.userName}
                        />
                    </FormGroup>
                )}
                <FormGroup
                    hasErrors={props.errors.has('password')}
                    label="Wachtwoord"
                    layout="vertical"
                >
                    <PasswordInput
                        disabled={props.isFormDisabled}
                        error={
                            props.errors.get('password')
                                ? translateKey('validation.' + props.errors.get('password'))
                                : undefined
                        }
                        name="password"
                        onBlur={() => props.onDoValidate('password')}
                        onChange={(e) => props.handleChange('password', e.target.value)}
                        value={props.login.password}
                    />
                </FormGroup>
                <FormButtonRow>
                    <Button
                        buttonRef={props.buttonRef}
                        color="secondary"
                        isDisabled={props.isFormDisabled || props.isLoginButtonDisabled}
                        type="submit"
                        width="full"
                    >
                        {translate.common.next}
                    </Button>
                </FormButtonRow>
            </Form>
            <Box
                alignItems="center"
                display="flex"
                flexDirection={['column', 'row']}
                justifyContent="center"
                sx={{ gap: [4, 0] }}
            >
                <Link
                    color="minimal"
                    fontWeight="normal"
                    size="sm"
                    to="/home?view=forgotPassword"
                    variant="link"
                >
                    Wachtwoord vergeten
                </Link>
                <Box
                    display={['none', 'inherit']}
                    mx={3}
                >
                    <Text color="minimal">{String.fromCharCode(8226)}</Text>
                </Box>
                <Link
                    color="minimal"
                    fontWeight="normal"
                    size="sm"
                    to="/home?view=forgotUsername"
                    variant="link"
                >
                    Gebruikersnaam vergeten
                </Link>
            </Box>
        </>
    );

    function handleFocusChangeOnError() {
        let inputName;
        if (props.errors.has('userName')) {
            inputName = 'userName';
        } else if (props.errors.has('password')) {
            inputName = 'password';
        }

        if (inputName) {
            const input = document.querySelector(`input[name=${inputName}]`) as HTMLInputElement;
            if (input) {
                input.focus();
            }
        }
    }

    function showErrorMessage() {
        if (!props.errorCode) {
            return null;
        } else if (props.errorCode === ValidationErrorCode.WRONG_PASSWORD_WARNING) {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    {`De combinatie van wachtwoord en gebruikersnaam klopt niet. Wanneer je nog ${props.remainingAttempts} keer je
                        inloggegevens verkeerd invult, moet je 15 minuten wachten voordat je het opnieuw kunt proberen.`}
                </Alert>
            );
        } else if (props.errorCode === ValidationErrorCode.USER_BLOCKED_TEMPORARILY) {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    Je kan niet meer inloggen voor 15 minuten. Probeer het daarna nog eens. Hulp nodig?{' '}
                    <Alert.Link
                        href="https://www.ksyos.nl/contact/faq"
                        openInNewTab={true}
                        variant="externalLink"
                    >
                        {' '}
                        Lees onze FAQ hier
                    </Alert.Link>
                </Alert>
            );
        } else if (props.errorCode === ValidationErrorCode.USER_INFORMATION_IS_CORRUPT) {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    Er is iets mis gegaan met het ophalen van je wachtwoord.{' '}
                    <Alert.Link
                        to="/home?view=forgotPassword"
                        variant="link"
                    >
                        Herstel je wachtwoord
                    </Alert.Link>{' '}
                    om in te kunnen loggen.
                </Alert>
            );
        } else if (props.errorCode === ValidationErrorCode.USER_HAS_NO_EMPLOYMENT) {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    Op dit moment heb je (nog) geen toegang tot een organisatie in Ksyos.
                    <br />
                    Heb je vragen? Bel{' '}
                    <Alert.Link
                        href="tel:+31 20 600 00 60"
                        variant="externalLink"
                    >
                        020-600 00 60
                    </Alert.Link>{' '}
                    of mail{' '}
                    <Alert.Link
                        href="mailto:info@ksyos.nl"
                        variant="externalLink"
                    >
                        info@ksyos.nl.
                    </Alert.Link>
                </Alert>
            );
        } else if (props.errorCode === ValidationErrorCode.USER_IS_NOT_ACTIVATED) {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    Je hebt je account nog niet geactiveerd.
                    <br />
                    Activeer je account vanuit de welkomst e-mail. Werkt deze niet meer? Neem contact op met Ksyos.
                </Alert>
            );
        } else if (props.errorCode === ValidationErrorCode.USERNAME_OR_PASSWORD_INCORRECT) {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    E-mail of wachtwoord klopt niet.
                </Alert>
            );
        } else {
            return (
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    Er is bij ons iets verkeerd gegaan. Probeer het later opnieuw, of laat ons je verder helpen. Bel{' '}
                    <Alert.Link
                        href="tel:+31 20 600 00 60"
                        variant="externalLink"
                    >
                        020-600 00 60
                    </Alert.Link>{' '}
                    of mail{' '}
                    <Alert.Link
                        href="mailto:info@ksyos.nl"
                        variant="externalLink"
                    >
                        info@ksyos.nl
                    </Alert.Link>
                    .
                </Alert>
            );
        }
    }
}
